import React, { useState } from "react";
import { FaSearch } from "react-icons/fa";
import { ExportRfqSelectedColumns } from "../../../../services/ProductService/AllProductList/AllProductServices";

const ExportRfqSelectedColumn = ({ onClose, onRfqSelectedId, onSuccess }) => {
    const [selectedFields, setSelectedFields] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filetype, setFiletype] = useState("");
    const [loading, setLoading] = useState(false);
    const [showsFileTypes, setShowsFileTypes] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const fields = {
        'Buyer / Seller': 'email',
        'Company Name': 'company_name',
        'Preferred Source Country': 'preferred_source_country',
        'Wanted': 'product_name',
        'Mobile No': 'mobile_number',
        'Short Description': 'short_description',
        'Product HSN Code': 'hsn_code',
        'Quantity': 'quantity',
        'Unit': 'unit',
        'Destination Port': 'destination_port',
        'Income Term': 'inco_term',
        'Payment Term': 'payment_term',
        'Target Price': 'target_price',
        'Negotiable': 'is_negotiable',
        'Attachement': 'attachment',
    };

    const toggleField = (label, value) => {
        setSelectedFields(prev =>
            prev.includes(value) ? prev.filter(f => f !== value) : [...prev, value]
        );
        setErrorMessage("");
    };

    const selectAllFields = () => {
        const allValues = Object.values(fields);
        setSelectedFields(allValues);
        setErrorMessage("");
    };

    const unselectAllFields = () => {
        setSelectedFields([]);
        setErrorMessage("Please select at least one field to export.");
    };

    const selectMandatoryFields = () => {
        const mandatoryLabels = ['Buyer / Seller', 'Company Name', 'Preferred Source Country', 'Wanted', 'Mobile No', 'Short Description'];
        const mandatoryValues = mandatoryLabels.map(label => fields[label]).filter(Boolean);
        setSelectedFields(mandatoryValues);
        setErrorMessage("");
    };

    const handleShowFileTypes = () => setShowsFileTypes(true);
    const handleClose = () => onClose();

    const handleExport = async () => {
        if (selectedFields.length === 0) {
            setErrorMessage("Please select at least one field to export.");
            return;
        }
        if (!filetype) {
            setErrorMessage("Please select a file type.");
            return;
        }

        setLoading(true);
        setErrorMessage("");

        const requestData = { "rfq_ids": onRfqSelectedId, "select_column": selectedFields, "file_type": filetype };
        console.log(requestData);

        try {
            console.log("Request Data:", requestData);

            const response = await ExportRfqSelectedColumns("POST", requestData, { responseType: 'blob' });

            if (!response || !response.data) {
                console.error("Invalid response:", response);
                return;
            }

            const isBlob = response.data instanceof Blob;
            if (!isBlob) {
                const text = await response.data.text();
                console.error("Expected Blob, got text:", text);
                return;
            }

            const isExcel = filetype === "Excel";
            const mimeType = isExcel
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "text/csv";
            const fileExtension = isExcel ? "xlsx" : "csv";

            const blob = new Blob([response.data], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `SelectProductDatas.${fileExtension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Error exporting file:", error);
        } finally {
            setLoading(false);
            onClose()
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center" onClick={handleClose}>
            <div className="bg-white p-6 rounded-lg shadow-lg w-2/4" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-gray-400 rounded-lg mb-5">
                    <div className="flex justify-between items-start px-4">
                        <h2 className="text-lg font-semibold">Export Selected Column</h2>
                        <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">&times;</button>
                    </div>
                </div>

                <div className="relative py-2">
                    <button
                        onClick={handleShowFileTypes}
                        className="border border-gray-300 rounded-lg px-3 py-2 w-full text-left"
                    >
                        {filetype || "Select File Type"}
                    </button>
                    {showsFileTypes && (
                        <div className="absolute w-full bg-white border mt-1 shadow-lg rounded-md z-10">
                            <div onClick={() => { setFiletype("Excel"); setShowsFileTypes(false); setErrorMessage(""); }}
                                className="px-4 py-2 cursor-pointer hover:bg-gray-200">
                                Excel
                            </div>
                            <div onClick={() => { setFiletype("CSV"); setShowsFileTypes(false); setErrorMessage(""); }}
                                className="px-4 py-2 cursor-pointer hover:bg-gray-200">
                                CSV
                            </div>
                        </div>
                    )}
                    {!filetype && errorMessage.includes("file type") && <p className="text-red-500 text-sm mt-1">{errorMessage}</p>}
                </div>

                <div className="flex justify-center my-5">
                    <div className="relative w-2/3">
                        <input
                            type="text"
                            placeholder="Search Fields..."
                            className="rounded-md border-none px-4 py-2 w-full bg-blue-100 outline-blue-100"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)} />
                        <FaSearch className="absolute right-3 top-3 text-gray-500" />
                    </div>
                </div>

                <div className="flex gap-2 mb-3">
                    <button onClick={selectAllFields} className="px-3 py-1 bg-gray-200 rounded">Select All</button>
                    <button onClick={selectMandatoryFields} className="px-3 py-1 bg-gray-200 rounded">Select Mandatory</button>
                    <button onClick={unselectAllFields} className="px-3 py-1 bg-gray-200 rounded">Unselect All</button>
                </div>

                <div className="border p-3 h-40 overflow-y-auto rounded-lg flex gap-2 flex-col">
                    {Object.entries(fields)
                        .filter(([label]) => label.toLowerCase().includes(searchTerm.toLowerCase())) // Filter fields based on search
                        .map(([label, value]) => (
                            <label key={value} className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedFields.includes(value)}
                                    onChange={() => toggleField(label, value)}
                                    className="mr-2 w-4 h-4"
                                />
                                {label}
                            </label>
                        ))}
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={handleClose} className="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                    <button onClick={handleExport} disabled={selectedFields.length === 0 || !filetype}
                        className={`px-4 py-2 rounded ${selectedFields.length === 0 || !filetype ? "bg-gray-300" : "bg-blue-500 text-white"}`}>
                        Export Data
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportRfqSelectedColumn;
